/** @jsx isolateComponent */

import { ReactElement, useState } from 'react';

import { Modal } from '@zapier/common-components';
import { isolateComponent } from '@zapier/common-utils';
import { useTrackingContext } from '@zapier/toolbox-tracking';

import { TrialType } from '@zapier/design-system-revenue/src/types';

import { CompanyTrialOffer } from './CompanyTrialOffer';
import { ProfessionalTrialOffer } from './ProfessionalTrialOffer';
import { TeamTrialOffer } from './TeamTrialOffer';
import { TrialTypeSelector } from './TrialTypeSelector';

type TrialWizardModalProps = {
  onClose: () => void;
};

type ContentType = TrialType | 'Root';

type ContentMap = {
  [x in ContentType]: ReactElement;
};

/**
 * A wizard modal that guides visitors to the appropriate trial.
 */
export const TrialWizardModal = ({ onClose }: TrialWizardModalProps) => {
  const { trackEvent } = useTrackingContext();
  const [selectedContentType, setSelectedContentType] = useState<ContentType>(
    'Root'
  );

  const contentMap: ContentMap = {
    Root: (
      <TrialTypeSelector
        onSelect={selectedTrialType =>
          setSelectedContentType(selectedTrialType)
        }
      />
    ),
    Professional: (
      <ProfessionalTrialOffer
        onDecline={() => setSelectedContentType('Root')}
      />
    ),
    Team: <TeamTrialOffer onDecline={() => setSelectedContentType('Root')} />,
    Company: (
      <CompanyTrialOffer onDecline={() => setSelectedContentType('Root')} />
    ),
  };

  return (
    <Modal
      closeOnOutsideClick={true}
      eventName="trial-wizard-modal-shown"
      fullStoryEventName="Viewed Trial Wizard Modal"
      onClose={() => {
        trackEvent('trial-wizard-modal-closed');
        onClose();
      }}
    >
      {contentMap[selectedContentType]}
    </Modal>
  );
};
